import type { GatewayBrowserClient } from "../gateway.ts";
import type { AgentsFleetGraphResult, SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import {
  formatFleetGraphError,
  mergeFleetGraphWithLive,
  stripEphemeralFleetEdges,
  type AgentFleetGraphSnapshot,
} from "../chat/agent-fleet-graph-model.ts";

export type AgentFleetGraphState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatSubagents: ChatSubagentCard[];
  sessions: SessionsListResult | null;
  agentFleetGraphLoading: boolean;
  agentFleetGraphRefreshing: boolean;
  agentFleetGraphError: string | null;
  agentFleetGraph: AgentFleetGraphSnapshot | null;
};

let fleetGraphLoadToken = 0;
let fleetGraphDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export function reapplyAgentFleetGraphLiveOverlay(state: AgentFleetGraphState): void {
  if (!state.agentFleetGraph) {
    return;
  }
  const base = stripEphemeralFleetEdges(state.agentFleetGraph);
  state.agentFleetGraph = mergeFleetGraphWithLive(
    base,
    state.sessionKey?.trim() ?? "",
    state.chatSubagents,
    state.sessions,
  );
}

export function scheduleAgentFleetGraphRefresh(state: AgentFleetGraphState, delayMs = 300): void {
  if (fleetGraphDebounceTimer) {
    clearTimeout(fleetGraphDebounceTimer);
  }
  fleetGraphDebounceTimer = setTimeout(() => {
    fleetGraphDebounceTimer = null;
    void loadAgentFleetGraph(state);
  }, delayMs);
}

export async function loadAgentFleetGraph(state: AgentFleetGraphState): Promise<void> {
  const client = state.client;
  if (!client || !state.connected) {
    state.agentFleetGraph = null;
    state.agentFleetGraphError = null;
    state.agentFleetGraphLoading = false;
    state.agentFleetGraphRefreshing = false;
    return;
  }

  const token = ++fleetGraphLoadToken;
  const initialLoad = !state.agentFleetGraph;
  if (initialLoad) {
    state.agentFleetGraphLoading = true;
  } else {
    state.agentFleetGraphRefreshing = true;
  }
  state.agentFleetGraphError = null;

  try {
    const sessionKey = state.sessionKey?.trim() ?? "";
    const res = await client.request<AgentsFleetGraphResult>("agents.fleet.graph", {
      ...(sessionKey ? { sessionKey } : {}),
    });
    if (token !== fleetGraphLoadToken) {
      return;
    }
    const snapshot: AgentFleetGraphSnapshot = {
      nodes: res.nodes,
      edges: res.edges.map((e) => ({ ...e, kind: e.kind })),
      sessionAgentId: res.sessionAgentId,
    };
    state.agentFleetGraph = mergeFleetGraphWithLive(
      snapshot,
      sessionKey,
      state.chatSubagents,
      state.sessions,
    );
    state.agentFleetGraphError = null;
  } catch (err) {
    if (token !== fleetGraphLoadToken) {
      return;
    }
    if (initialLoad) {
      state.agentFleetGraph = null;
    }
    state.agentFleetGraphError = formatFleetGraphError(err);
  } finally {
    if (token === fleetGraphLoadToken) {
      state.agentFleetGraphLoading = false;
      state.agentFleetGraphRefreshing = false;
    }
  }
}
